<script setup lang="ts">
import { computed } from "vue";
import type { ButtonSize, SecondaryButtonTone } from "@/components/shared/buttons/types";

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    disabled?: boolean;
    type?: "button" | "submit" | "reset";
    size?: ButtonSize;
    tone?: SecondaryButtonTone;
    compact?: boolean;
    prependIcon?: string;
    /** Renders a square, label-less button showing only `prependIcon`. */
    iconOnly?: boolean;
  }>(),
  {
    loading: false,
    disabled: false,
    type: "button",
    size: "large",
    tone: "brand",
    compact: false,
    prependIcon: undefined,
    iconOnly: false,
  }
);

const emit = defineEmits<{ click: [event: MouseEvent] }>();

const variantClasses = computed(() => [
  `secondary-button--${props.tone}`,
  props.compact ? "secondary-button--compact" : "",
  props.iconOnly ? "secondary-button--icon-only" : "",
]);
</script>

<template>
  <v-btn
    v-if="props.iconOnly"
    key="icon-only"
    :type="props.type"
    :loading="props.loading"
    :disabled="props.disabled"
    :size="props.size"
    :icon="props.prependIcon"
    density="comfortable"
    :ripple="false"
    variant="flat"
    class="orbis-btn secondary-button tw:inline-flex tw:items-center tw:justify-center"
    :class="variantClasses"
    @click="emit('click', $event)"
  />
  <v-btn
    v-else
    key="full"
    :type="props.type"
    :loading="props.loading"
    :disabled="props.disabled"
    :size="props.size"
    :prepend-icon="props.prependIcon"
    :ripple="false"
    variant="flat"
    class="orbis-btn secondary-button tw:inline-flex tw:items-center tw:gap-2 tw:text-sm"
    :class="variantClasses"
    @click="emit('click', $event)"
  >
    <slot />
  </v-btn>
</template>

<style scoped>
/* Layout, gap and type scale live as `tw:` utilities on the element; what stays
   here is what Tailwind cannot express against Vuetify — the token-driven
   palette, and the `!important` needed to outrank `.v-btn`'s own padding,
   border and radius. Motion is shared via `.orbis-btn` in style.css.
   Design spec: 44px tall, 0 22px padding, 11px radius, weight 600.
   `--btn-px` stays a fallback so an ancestor can retune it. */
.secondary-button {
  /* Spec's hover moves color + border only, so the lift and press are opted out. */
  --btn-lift: 0px;
  --btn-press-scale: 1;

  padding: 0 var(--btn-px, 22px) !important;

  border: 1px solid var(--btn-hairline) !important;
  border-radius: var(--btn-radius) !important;

  background: var(--btn-surface) !important;
  color: var(--btn-ink-muted) !important;

  font-size: var(--btn-fs);
  font-weight: 600;

  box-shadow: var(--btn-shadow);
}

.secondary-button.secondary-button--compact {
  --btn-px: 14px;
  --btn-fs: 13px;
  --btn-hairline: rgba(var(--v-theme-on-surface), 0.06);
}

.secondary-button.secondary-button--icon-only {
  min-width: 0 !important;
  padding: 0 !important;
}

/* Vuetify paints hover/focus/active/disabled with a `currentColor` overlay on
   top of the background this variant sets itself, which showed up as a grey
   slab when disabled and a teal wash on hover. Overlay off, ripple off in the
   template, and the disabled palette spelled out below. */
.secondary-button :deep(.v-btn__overlay),
.secondary-button :deep(.v-btn__underlay) {
  display: none;
}

.secondary-button:hover:not(:disabled) {
  border-color: var(--btn-hairline-strong) !important;
  color: var(--btn-ink) !important;

  box-shadow: var(--btn-shadow);
}

.secondary-button.v-btn--disabled {
  opacity: 1 !important;

  border-color: rgba(var(--v-theme-on-surface), 0.09) !important;
  background: var(--btn-surface) !important;
  color: rgba(var(--v-theme-on-surface), 0.38) !important;

  box-shadow: none;
}

.secondary-button--brand:hover:not(:disabled) {
  color: var(--btn-ink) !important;
  border-color: var(--btn-hairline-strong) !important;
}

.secondary-button--neutral {
  color: var(--btn-ink-muted) !important;
}

.secondary-button--neutral:hover:not(:disabled) {
  color: var(--btn-ink) !important;
  border-color: var(--btn-hairline-strong) !important;
  background: var(--btn-surface-hover) !important;
}

.secondary-button--danger {
  border-color: rgba(var(--v-theme-error), 0.4) !important;
  color: var(--btn-danger) !important;
}

/* Quiet at rest, filled on hover — the affordance the patient card's row actions use.
   Declared after the shared `:hover` rule above so it wins the tie on specificity. */
.secondary-button--ink {
  color: rgb(var(--v-theme-primary)) !important;
}

.secondary-button--ink:hover:not(:disabled) {
  transform: translateY(-1px) !important;

  border-color: rgb(var(--v-theme-primary)) !important;
  background: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-primary)) !important;

  box-shadow: 0 2px 5px rgba(var(--v-theme-shadow), 0.08) !important;
}

.secondary-button--danger:hover:not(:disabled) {
  color: var(--btn-danger) !important;
  border-color: var(--btn-danger) !important;
  background: var(--btn-danger-surface-hover) !important;
}
</style>
